<template>
  <div>
    <v-row v-if="isLoading">
      <v-col>
        <h-loading />
      </v-col>
    </v-row>

    <div v-if="!isLoading && Object.keys(hostOnboardingStepData).length">
      <v-divider class="mb-2" />

      <v-row>
        <v-col>
          <span class="text-h6 grey--text">
            {{ 'Passo atual: ' + $t(`hostOnboarding.types.${hostOnboardingStepData.type}`) }}
          </span>
        </v-col>
      </v-row>

      <v-row v-if="firstStep">
        <v-col>
          <v-alert v-if="!Object.keys(hostBackgroundCheck).length" color="orange lighten-5" class="warning--text">
            <div class="d-flex ma-2 mb-4">
              <v-icon color="warning">mdi-information-outline</v-icon>
              <span class="ml-4">Verificação de histórico pessoal do Host ainda não foi feita.</span>
            </div>
            <div class="d-flex justify-center">
              <v-btn
                :loading="hostVerificationLoading"
                @click="openVerifyHostOnboardingModal"
                elevation="0"
                block
                color="primary"
              >
                Verificar
              </v-btn>
            </div>
          </v-alert>

          <div class="d-flex justify-center align-center" v-if="Object.keys(hostBackgroundCheck).length">
            <v-alert v-if="hostBackgroundCheck.status != 'COMPLETED'" color="blue lighten-5" class="primary--text">
              <div class="d-flex ma-2 mb-4">
                <v-icon color="primary">mdi-timer-sand</v-icon>
                <span class="ml-4">Verificação de histórico pessoal do Host está em andamento</span>
              </div>
            </v-alert>

            <div v-if="hostBackgroundCheck.status == 'COMPLETED'">
              <v-progress-circular
                :rotate="90"
                :size="120"
                :width="10"
                :value="hostScore * 10"
                :color="hostScoreColor"
              >
                <div>
                  <span class="text-h4 font-weight-bold d-flex justify-center">
                    {{ leadingZero(hostScore, 2)}}
                  </span>
                  <span class="grey--text">
                    pontos
                  </span>
                </div>

              </v-progress-circular>
            </div>

            <div v-if="hostBackgroundCheck.status == 'COMPLETED'" class="d-block ml-4">
              <span>
                Nota do histórico <br>
                pessoal do Host
              </span>
              <span class="d-flex justify-start primary--text mt-2 pointer" @click="showFile(hostBackgroundCheck.truora_attachment.url)">
                Baixar detalhes <v-icon color="primary" class="ml-1" small>mdi-download</v-icon>
              </span>
            </div>
          </div>
        </v-col>
      </v-row>

      <v-divider v-if="firstStep" class="my-2" />

      <div v-if="Object.keys(host).length">
        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('host.label.name') }}
            </h6>
            <span class="font-weight-medium">
              {{ host.name }}
            </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('host.label.username') }}
            </h6>
            <span class="font-weight-medium">
              {{ host.username }}
            </span>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('host.label.cpf') }}
            </h6>
            <span class="font-weight-medium">
              {{ host.cpf ? formatDocument(host.cpf) : '---' }}
            </span>
          </v-col>

          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('host.label.rg') }}
            </h6>
            <span class="font-weight-medium">
              {{ host.rg ? host.rg : '---' }}
            </span>
          </v-col>
        </v-row>
      </div>

      <v-divider v-if="hostOnboardingStepData.course" class="my-2" />

      <div v-if="hostOnboardingStepData.course">
        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('hostOnboarding.label.course') }}
            </h6>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              :items="formattedHostOnboardingCoursePercentages"
              item-text="name"
              item-value="value"
              hide-selected
              v-model="hostOnboardingStepData.course_percentage"
              @change="updateCoursePercentage"
              outlined
              :label="$t('hostOnboarding.label.progress')"
              :disabled="isLoadingButton"
              hide-details
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('hostOnboarding.label.name') }}
            </h6>
            <span class="font-weight-medium">
              {{ `Curso de ${$t('type.' + hostOnboardingStepData.course.type)}` }}
            </span>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('hostOnboarding.label.description') }}
            </h6>
            <span class="font-weight-medium">
              {{ hostOnboardingStepData.course.description ? hostOnboardingStepData.course.description : '---' }}
            </span>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <h6 class="text-uppercase grey--text">
              {{ $t('hostOnboarding.label.link') }}
            </h6>
            <span @click="showFile(hostOnboardingStepData.course.link)" class="pointer primary--text text-decoration-underline">
              {{ hostOnboardingStepData.course.link }}
            </span>
          </v-col>
        </v-row>
      </div>

      <v-divider v-if="!lastStep && !disapproved" class="my-2" />

      <v-row no-gutters v-if="disapproved">
        <v-col>
          <v-alert color="error lighten-5" class="error--text">
            <v-row no-gutters>
              <v-col>
                <h6 class="text-uppercase">Reprovado em</h6>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <span>
                  {{formatDateWithoutTime(hostOnboardingStepData.date_created)}}
                </span>
              </v-col>
            </v-row>

            <v-row no-gutters class="mt-4">
              <v-col>
                <h6 class="text-uppercase">Motivo</h6>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <span>
                  {{
                    hostOnboardingStepData.disapprove_type ?
                    $t(`hostOnboarding.disapproveTypes.${hostOnboardingStepData.disapprove_type}`) :
                    '---'
                  }}
                </span>
              </v-col>
            </v-row>

            <v-row no-gutters class="mt-4">
              <v-col>
                <h6 class="text-uppercase">Comentário</h6>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col>
                <span>
                  {{hostOnboardingStepData.disapprove_description ? hostOnboardingStepData.disapprove_description : '---'}}
                </span>
              </v-col>
            </v-row>
          </v-alert>
        </v-col>
      </v-row>

      <v-row v-if="!lastStep && !disapproved && !contractStep">
        <v-col>
          <h6 class="text-uppercase grey--text mb-5">
            {{ $t('hostOnboarding.label.attachments') }}
          </h6>
          <div v-if="hostOnboardingStepData.files && hostOnboardingStepData.files.length">
            <v-row class="my-2" v-for="file in hostOnboardingStepData.files" :key="file.id" no-gutters>
              <v-col>
                <span @click="showFile(file.url)" class="pointer primary--text text-decoration-underline">
                  <v-icon class="mb-1 mr-1" color="primary" small >
                    mdi-download
                  </v-icon>
                  {{`${file.filename}`}}
                </span>
                <span class="body-2 font-weight-medium ml-2">
                  {{formatDate(file.date_created)}}
                </span>
              </v-col>
            </v-row>
          </div>
          <div v-else>
            <v-row no-gutters>
              <v-col>
                <span class="grey--text">
                  ---
                </span>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <v-divider v-if="!lastStep && !disapproved && !contractStep" class="my-2" />

      <v-row v-if="!lastStep && !disapproved && !contractStep">
        <v-col>
          <h6 class="text-uppercase grey--text mb-5">
            {{ $t('hostOnboarding.label.feedbacks') }}
          </h6>
          <div>
            <HostOnboardingFeedbacks
              :key="hostOnboardingStepData.id"
              ref="feedbacks"
              :host-name="host.name"
              :feedbacks="hostOnboardingStepData.feedbacks"
            />
            <v-form v-on:submit.prevent ref="messageForm" v-model="valid">
              <v-row no-gutters>
                <v-text-field
                  placeholder="Escreva uma mensagem..."
                  append-icon="mdi-send"
                  v-model="feedbackMessage"
                  :rules="feedbackMessageRule"
                  @click:append="sendFeedback"
                  @keydown.enter="sendFeedback"
                  outlined
                />
              </v-row>
            </v-form>
          </div>
        </v-col>
      </v-row>

      <div v-if="contractStep">
        <v-row no-gutters v-for="agreement in hostOnboardingStepData.agreements" :key="agreement.id">
          <v-col>
            <span @click="openShowAgreementModal(agreement.type)" class="d-flex justify-start primary--text mt-2 pointer">
                {{ agreement.type == 'CONTRACT' ? 'Contrato' : agreement.type == 'TERMS' ? 'Termos de uso' : '---' }}
                <v-icon color="primary" class="ml-1" small>mdi-open-in-new</v-icon>
              </span>
          </v-col>
        </v-row>
      </div>
    </div>

    <v-row v-if="!isLoading && !Object.keys(hostOnboardingStepData).length">
      <v-col>
        <EmptyState />
      </v-col>
    </v-row>

    <ModalVerifyHostOnboarding :host="host" />
    <ModalShowAgreement />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { catalogDate, catalogDateWithoutTime, zeroPad } from '@/util/catalog'
import { formatDocument } from '@/util/document'
import EmptyState from '@/components/app/EmptyState'
import ModalVerifyHostOnboarding from '@/components/host/modals/ModalVerifyHostOnboarding'
import ModalShowAgreement from '@/components/host/modals/ModalShowAgreement'
import HostOnboardingFeedbacks from '@/components/host/HostOnboardingFeedbacks'

export default {
  name: 'HostOnboardingResume',

  props: {
    host: {
      type: Object,
      default: {},
    },
  },

  data() {
    return {
      formatDocument,
      formatDate: catalogDate,
      leadingZero: zeroPad,
      formatDateWithoutTime: catalogDateWithoutTime,
      hostVerificationLoading: false,
      feedbackMessage: '',
      currentAgreement: null,
      valid: true,
      feedbackMessageRule: [(v) => (v ? v.length <= 250 : true) || 'Tamanho máximo excedido'],
    }
  },

  components: {
    EmptyState,
    ModalVerifyHostOnboarding,
    ModalShowAgreement,
    HostOnboardingFeedbacks
  },

  watch: {
    hostOnboardingStepData() {
      if (this.hostOnboardingStepData.feedbacks.length) {
        this.hostOnboardingStepData.feedbacks = this.hostOnboardingStepData.feedbacks.sort((a,b) => this.sortByDateCreated(b,a))
      }
      if (this.hostOnboardingStepData.files.length) {
        this.hostOnboardingStepData.files = this.hostOnboardingStepData.files.sort((a,b) => this.sortByDateCreated(a,b))
      }
    }
  },

  computed: {
    ...mapGetters(['isLoading', 'isLoadingButton', 'hostOnboardingStepData', 'hostBackgroundCheck', 'hostOnboardingCoursePercentages']),

    firstStep() {
      return this.hostOnboardingStepData.type == 'REGISTRATION'
    },

    lastStep() {
      return this.hostOnboardingStepData.type == 'DONE'
    },

    contractStep() {
      return this.hostOnboardingStepData.type == 'AGREEMENT'
    },

    disapproved() {
      return this.hostOnboardingStepData.type == 'DISAPPROVED'
    },

    hostScore() {
      return Math.ceil((this.hostBackgroundCheck?.score) * 10)
    },

    hostScoreColor() {
      return this.hostScore <  5 ? 'error' : this.hostScore < 8 ? 'warning' : 'success'
    },

    formattedHostOnboardingCoursePercentages() {
      return this.hostOnboardingCoursePercentages.map((item) => item = {name: this.$t(`hostOnboarding.coursePercentage.${item}`), value: item})
    },
  },

  methods: {
    ...mapActions(['openModal', 'addFeedbackMessage', 'changeCoursePercentage']),
    ...mapMutations(['setHostBackgroundCheck']),

    showFile(url) {
      window.open(url, '_blank')
    },

    sortByDateCreated(a,b) {
      return new Date(b.date_created) - new Date(a.date_created)
    },

    async sendFeedback() {
      if (!this.valid || this.feedbackMessage.trim().length == 0 ) {
        return
      }

      var request = {
        host_id: this.host.id,
        message: this.feedbackMessage
      }

      await this.addFeedbackMessage(request)
      this.feedbackMessage = ''
      this.scrollFeedbacksToBottom()
    },

    scrollFeedbacksToBottom() {
      this.$refs['feedbacks'].$refs['scroll-card'].$el.scrollTop = 1000000
    },

    getRandomInt(min, max) {
      min = Math.ceil(min);
      max = Math.floor(max);
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    async sleep(msec) {
      return new Promise(resolve => setTimeout(resolve, msec));
    },

    openVerifyHostOnboardingModal() {
      this.openModal('VERIFY_HOST_ONBOARDING')
    },

    openShowAgreementModal(currentAgreement) {
      this.currentAgreement = currentAgreement
      this.openModal('SHOW_HOST_ONBOARDING_AGREEMENT')
    },

    updateCoursePercentage() {
      let request = {
        host_id: this.host.id,
        course_percentage: this.hostOnboardingStepData.course_percentage
      }

      this.changeCoursePercentage(request)
    }
  },
}
</script>

<style scoped>
  .pointer {
    cursor: pointer !important
  }
</style>
